import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import toaster from '../utils/toaster';
import useSlackAgentConnection from './hooks/useSlackAgentConnection';
import SlackConnectModal from './components/SlackConnectModal';
import {
    GENERIC_ERROR_KEY,
    SLACK_AGENT_EVENTS,
    TOAST_AUTO_CLOSE_MS,
    toastKey,
    trackSlackAgentEvent,
} from './constants';
import { SlackAgentContext } from './slackAgentContext';

const SlackAgentProvider = ({ children, translate }) => {
    const connection = useSlackAgentConnection();
    const { status, busy, connectWorkspace } = connection;
    const needsWorkspace = Boolean(status?.available && !status.workspace);

    const [modalSurface, setModalSurface] = useState(null);

    useEffect(() => {
        // Coming back from Slack restores the page from bfcache with the explainer still open.
        const closeOnRestore = event => {
            if (event.persisted) setModalSurface(null);
        };
        window.addEventListener('pageshow', closeOnRestore);
        return () => window.removeEventListener('pageshow', closeOnRestore);
    }, []);

    const connectNow = useCallback(
        async surface => {
            trackSlackAgentEvent(SLACK_AGENT_EVENTS.INSTALL_CLICKED, { surface });
            try {
                await connectWorkspace();
            } catch {
                toaster.error(translate(toastKey(GENERIC_ERROR_KEY)), { autoClose: TOAST_AUTO_CLOSE_MS });
            }
        },
        [connectWorkspace, translate]
    );

    const openConnect = useCallback(surface => {
        trackSlackAgentEvent(SLACK_AGENT_EVENTS.CONNECT_MODAL_OPENED, { surface });
        setModalSurface(surface);
    }, []);

    const cancelConnect = useCallback(() => {
        trackSlackAgentEvent(SLACK_AGENT_EVENTS.CONNECT_MODAL_CANCELLED, { surface: modalSurface });
        setModalSurface(null);
    }, [modalSurface]);

    const value = useMemo(() => ({ connection, needsWorkspace, connectNow, openConnect }), [
        connection,
        needsWorkspace,
        connectNow,
        openConnect,
    ]);

    return (
        <SlackAgentContext.Provider value={value}>
            {children}
            <SlackConnectModal
                open={modalSurface != null}
                busy={busy}
                onContinue={() => connectNow(modalSurface)}
                onCancel={cancelConnect}
            />
        </SlackAgentContext.Provider>
    );
};

SlackAgentProvider.propTypes = {
    children: PropTypes.node,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SlackAgentProvider);
